import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  FolderOpen, 
  FileText,
  Briefcase,
  Users,
  MessageSquare,
  Settings,
  Calendar,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { cn } from '../lib/utils';

const Sidebar = ({ onSectionChange, activeSection = 'dashboard' }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openSections, setOpenSections] = useState({
    'comunicacion-socios': false,
    configuracion: false
  });

  const toggleSection = (section) => {
    if (isCollapsed) return;
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSectionClick = (sectionId) => {
    if (onSectionChange) {
      onSectionChange(sectionId);
    }
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      hasSubmenu: false
    },
    {
      id: 'preparacion-reportes',
      label: 'Preparación de Reportes',
      icon: FileText,
      hasSubmenu: false
    },
    {
      id: 'gestion-dealflow',
      label: 'Gestión Dealflow',
      icon: Briefcase,
      hasSubmenu: false
    },
    {
      id: 'gestion-participadas',
      label: 'Gestión Participadas',
      icon: Users,
      hasSubmenu: false
    },
    {
      id: 'comunicacion-socios',
      label: 'Comunicación con Socios',
      icon: MessageSquare,
      hasSubmenu: true,
      submenu: [
        { id: 'listado-socios', label: 'Listado de Socios' }
      ]
    },
    {
      id: 'repositorio',
      label: 'Repositorio de Documentos',
      icon: FolderOpen,
      hasSubmenu: false
    },
    {
      id: 'calendario',
      label: 'Calendario',
      icon: Calendar,
      hasSubmenu: false
    },
    {
      id: 'configuracion',
      label: 'Configuración',
      icon: Settings,
      hasSubmenu: true,
      submenu: [
        { id: 'perfil', label: 'Perfil' },
        { id: 'preferencias', label: 'Preferencias' },
        { id: 'integraciones', label: 'Integraciones' },
        { id: 'plantillas-email', label: 'Plantillas de Email' }
      ]
    }
  ];

  return (
    <>
      {/* Botón toggle para móvil */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 md:hidden"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {/* Overlay para móvil */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-full bg-card border-r border-border/50 shadow-sm z-50 transition-all duration-300",
          isOpen ? "translate-x-0" : "-translate-x-full",
          "md:translate-x-0",
          isCollapsed ? "w-20" : "w-72"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header del Sidebar */}
          <div className={cn(
            "flex items-center border-b border-border/50 relative",
            isCollapsed ? "justify-center p-4" : "justify-between p-4"
          )}>
            {isCollapsed ? (
              <div className="w-full flex justify-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 hidden md:flex"
                  onClick={() => setIsCollapsed(!isCollapsed)}
                >
                  <ChevronRight
                    className="h-4 w-4 transition-transform rotate-180"
                  />
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 min-w-0">
                  {/* En móvil, mostrar botón de cerrar cuando el menú está abierto, en desktop mostrar logo */}
                  {isOpen ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 md:hidden"
                      onClick={() => setIsOpen(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  ) : null}
                  <div className="flex items-center gap-2 flex-shrink-0 hidden md:flex">
                    <img src="/favicon-white.svg" alt="Logo" className="w-10 h-10 object-contain" />
                    <span className="font-semibold text-base tracking-wide whitespace-nowrap">JARVIS</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hidden md:flex"
                  onClick={() => setIsCollapsed(!isCollapsed)}
                >
                  <ChevronRight
                    className="h-4 w-4 transition-transform"
                  />
                </Button>
              </>
            )}
          </div>

          {/* Menú */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              
              if (item.hasSubmenu) {
                return (
                  <Collapsible
                    key={item.id}
                    open={!isCollapsed && openSections[item.id]}
                    onOpenChange={() => toggleSection(item.id)}
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        variant={isActive ? "secondary" : "ghost"}
                        className={cn(
                          "w-full h-10 text-sm font-normal",
                          isCollapsed ? "justify-center" : "justify-start gap-2"
                        )}
                        title={isCollapsed ? item.label : undefined}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        {!isCollapsed && (
                          <>
                            <span className="flex-1 text-left">{item.label}</span>
                            <ChevronRight
                              className={cn(
                                "h-4 w-4 transition-transform flex-shrink-0",
                                openSections[item.id] && "rotate-90"
                              )}
                            />
                          </>
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    {!isCollapsed && (
                      <CollapsibleContent className="pl-6 space-y-1 mt-1">
                        {item.submenu.map((subItem) => {
                          return (
                            <Button
                              key={subItem.id}
                              variant="ghost"
                              onClick={() => handleSectionClick(subItem.id)}
                              className={cn(
                                "w-full justify-start gap-2 h-9 text-sm font-normal text-muted-foreground hover:text-foreground",
                                activeSection === subItem.id && "bg-secondary text-foreground"
                              )}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground flex-shrink-0" />
                              {subItem.label}
                            </Button>
                          );
                        })}
                      </CollapsibleContent>
                    )}
                  </Collapsible>
                );
              }

              return (
                <Button
                  key={item.id}
                  variant={isActive ? "secondary" : "ghost"}
                  onClick={() => handleSectionClick(item.id)}
                  className={cn(
                    "w-full h-10 text-sm font-normal",
                    isCollapsed ? "justify-center" : "justify-start gap-2"
                  )}
                  title={isCollapsed ? item.label : undefined}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  {!isCollapsed && <span>{item.label}</span>}
                </Button>
              );
            })}
          </nav>

          {/* Footer del Sidebar */}
          {!isCollapsed && (
            <div className="p-4 border-t border-border/50">
              <div className="text-xs text-muted-foreground">
                <p className="font-medium">Versión 1.0.0</p>
                <p className="mt-1">© 2024</p>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;

